/**
 * "QA-142" — the name a finding has in a sentence.
 *
 * A finding's id (`fnd_` + 21 random chars) is the right key for a database
 * and the wrong one for a person. It cannot be read off a screen, said in a
 * standup, or typed into a chat, so the console gave people nothing to point
 * at: the only handle on the board was a hyperlink hidden under a timestamp,
 * and referencing a row meant copying a URL and cutting the prefix off it.
 *
 * So every finding also gets a short ORG-SCOPED number. Org-scoped rather than
 * per-project on purpose: the board is org-wide, `qa.getFinding` reads with an
 * org and no project, and a per-project counter behind one shared "QA-" prefix
 * would make QA-7 mean two different findings the moment an org has a second
 * project. One counter per org, one prefix, one meaning.
 *
 * The two namespaces are disjoint — a `fnd_` id never parses as a ref, and a
 * ref never looks like an id — which is what lets `findingScope` accept either
 * without asking the caller which one it is holding.
 *
 * Vanilla TS with no imports, so the widget bundle can take it (rule 6).
 */

/** Prefix the number wears everywhere it is shown. */
export const FINDING_REF_PREFIX = "QA-";

/** `142` → `QA-142`. Null in, null out: a finding read from a server that
 *  predates the column has no ref, and the UI simply shows none. */
export function formatFindingRef(
  ref: number | null | undefined,
): string | null {
  return typeof ref === "number" && Number.isInteger(ref) && ref > 0
    ? `${FINDING_REF_PREFIX}${ref}`
    : null;
}

/**
 * `QA-142` → `142`, and null for anything that is not one.
 *
 * Forgiving about how it was written down, because this parses what a HUMAN
 * pasted: the prefix in any case, a `#`, or the bare number someone read off a
 * card. Strict about what it accepts, because the answer routes a lookup — a
 * `fnd_…` id has to come back null here or it would be looked up as a number.
 */
export function parseFindingRef(
  value: string | null | undefined,
): number | null {
  if (!value) return null;
  const trimmed = value.trim();
  const digits = /^(?:qa-|#)?(\d{1,9})$/i.exec(trimmed);
  if (!digits) return null;
  const ref = Number(digits[1]);
  return Number.isInteger(ref) && ref > 0 ? ref : null;
}
